import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {
    IconToggleSwitch,
    ToggleBar,
    ToggleBarIcon,
    toggleSwitchButtonSx,
    toggleSwitchSx
} from "../../../components/ToggleBar.tsx";
import {SortOrder, useBundleById} from "../../../rpc/webrpc-client.gen.ts";
import {useCallback, useEffect, useState} from "react";
import {BundleDiaryView, BundleTimelineRelay} from "../../../bundle/timeline.tsx";
import {useNaviScratchBlockDispatch} from "../../../navi/Navi.tsx";
import {facetIcons} from "../../../components/Facet.tsx";
import {defaultPrefs} from "../../../util/defaultPrefs.tsx";
import {useBomb} from "../../../bomb/bomb.tsx";
import {sortOrderIcons} from "../../../tq/tqutil.tsx";
import {Box, Button, Sheet, Stack, ToggleButtonGroup, useTheme} from "@mui/joy";
import {BundleCapsule} from "../../../bundle/capsule.tsx";
import {TimelineStampInput} from "../../../stamp/timelineStampInput.tsx";
import {useStampBundleMutation} from "../../../tq/tqx.tsx";
import {GenericFlavorlessStampSpawnFood} from "../../../stamp/genericFlavorlessStampSpawnFood.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {usePreferences} from "../../../agent/usePreferences.ts";
import {useSelf} from "../../../agent/useSelf.ts";

function BundleTimelinePage() {
    const {id: bundleId} = Route.useParams();
    const {data: prefs} = usePreferences();
    const [sortOrder, setSortOrder] = useState<SortOrder>((prefs ?? defaultPrefs).bundleSortOrder);
    const navigate = useNavigate();
    const bomb = useBomb();
    const bundleTqrv = useBundleById({bundleId});
    const bundle = bundleTqrv.data;
    const theme = useTheme();
    const selfTqrv = useSelf();
    const isInspector = !!selfTqrv.data?.inspectorFor;
    const isSupervisor = !!selfTqrv.data?.supervisorFor;

    const {mutateAsync: performStamp} = useStampBundleMutation();
    const doStamp = useCallback((gf: GenericFlavorlessStampSpawnFood) =>
        performStamp({bundleId, ...gf}), [bundleId, performStamp]);

    const dispatchScratchNaviItem = useNaviScratchBlockDispatch();
    useEffect(() => {
        if (bundle) {
            dispatchScratchNaviItem({
                to: Route.to,
                params: {id: bundleId},
                icon: {icon: facetIcons.timeline},
                children: bundle.displayName,
            });
        }
    }, [dispatchScratchNaviItem, bundle, bundleId]);

    return <PageShell>
        <BundleTimelineRelay bundleId={bundleId}/>

        <PageHeader>
            <Stack direction="column">
                <TopBar/>

                <ToggleBar>
                    <ToggleButtonGroup color="primary" variant="soft"
                                       value="timeline" onChange={(_, next) => {
                        if (next) {
                            navigate({to: `/bundle/$id/${next}`, params: {id: bundleId}})
                                .catch(bomb);
                        }
                    }} sx={toggleSwitchSx}>
                        {isInspector || isSupervisor
                            ? <Button sx={toggleSwitchButtonSx} value="inspect">
                                <ToggleBarIcon icon={facetIcons.inspect}/>
                            </Button>
                            : <Button sx={toggleSwitchButtonSx} value="cargo">
                                <ToggleBarIcon icon={facetIcons.cargo}/>
                            </Button>}
                        <Button sx={toggleSwitchButtonSx} value="timeline">
                            <ToggleBarIcon icon={facetIcons.timeline}/>
                        </Button>
                    </ToggleButtonGroup>

                    <IconToggleSwitch icons={sortOrderIcons} value={sortOrder} dispatchValue={(next) => {
                        setSortOrder(next);
                    }}/>
                </ToggleBar>

                <Sheet sx={{width: "100%", justifyItems: "stretch"}}>
                    <Box sx={{color: theme.palette.primary[800], bgcolor: theme.palette.primary[100]}}>
                        {bundle
                            ? <BundleCapsule bundle={bundle} sx={{fontSize: theme.fontSize.xl}}/>
                            : null}
                    </Box>
                    <TimelineStampInput addStamp={doStamp}/>
                </Sheet>
            </Stack>
        </PageHeader>

        <PageBody>
            <BundleDiaryView id="bundle-diary-view" bundleId={bundleId}
                             sortOrder={sortOrder}/>
        </PageBody>
    </PageShell>
}

export const Route = createFileRoute("/_auth/bundle/$id/timeline")({
    component: BundleTimelinePage,
});

